import { createAction, createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { OrganizationApi } from "~/functions/api/organization";
import type { UpdateOrganization } from "~/interface/organization";
import type { User } from "~/interface/user";
import type { RootState } from "~/store";
import { setOrganization } from "./auth";

type DisplayRole = "管理者" | "プロジェクト管理者" | "ユーザー" | "ゲスト";

interface OrganizationState {
  members: (User & { displayRole: DisplayRole })[];
  guests: (User & { displayRole: DisplayRole })[];
  // 組織情報
  organization: {
    id: string;
    name: string;
    url: string;
    payment_id?: string;
    rootUser: {
      id: string;
      firstName: string;
      lastName: string;
      displayName: string;
    };
  };
  // 編集用のコピー
  copyOrganization: {
    id: string;
    name: string;
    url: string;
    payment_id?: string;
    rootUser: {
      id: string;
      firstName: string;
      lastName: string;
      displayName: string;
    };
  };
  isEditable: boolean;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
}

const initialState: OrganizationState = {
  members: [],
  guests: [],
  organization: {
    id: "",
    name: "",
    url: "",
    payment_id: "",
    rootUser: {
      id: "",
      firstName: "",
      lastName: "",
      displayName: "",
    },
  },
  copyOrganization: {
    id: "",
    name: "",
    url: "",
    payment_id: "",
    rootUser: {
      id: "",
      firstName: "",
      lastName: "",
      displayName: "",
    },
  },
  isEditable: false,
  isLoading: false,
  isSaving: false,
  error: null,
};

// Helper function to rename member roles
const handleRenameMemberRole = (role: User["role"]): DisplayRole => {
  switch (role) {
    case "admin":
      return "管理者";
    case "manager":
      return "プロジェクト管理者";
    case "member":
      return "ユーザー";
    case "guest":
      return "ゲスト";
  }
};

// APIインスタンスの作成
const organizationApi = new OrganizationApi();

// 組織メンバーの取得
export const fetchOrganizationMembers = createAsyncThunk(
  "organization/fetchMembers",
  async (organizationId: string, { rejectWithValue }) => {
    try {
      const response = await organizationApi.getMembers(organizationId);
      if (response === null) {
        return rejectWithValue("メンバーの取得に失敗しました");
      }
      return response;
    } catch (_error) {
      return rejectWithValue("メンバーの取得中にエラーが発生しました");
    }
  },
);

// 組織ゲストの取得
export const fetchOrganizationGuests = createAsyncThunk(
  "organization/fetchGuests",
  async (organizationId: string, { rejectWithValue }) => {
    try {
      const response = await organizationApi.getGuests(organizationId);
      if (response === null) {
        return rejectWithValue("ゲストの取得に失敗しました");
      }
      return response;
    } catch (_error) {
      return rejectWithValue("ゲストの取得中にエラーが発生しました");
    }
  },
);

// 組織情報の取得
export const fetchOrganization = createAsyncThunk(
  "organization/fetch",
  async (organizationId: string, { rejectWithValue, dispatch }) => {
    try {
      const response = await organizationApi.get(organizationId);
      if (response === null) {
        return rejectWithValue("組織情報の取得に失敗しました");
      }

      // 成功時にauthSliceのsetOrganizationを呼び出し
      dispatch(setOrganization(response));

      return response;
    } catch (_error) {
      return rejectWithValue("組織情報の取得中にエラーが発生しました");
    }
  },
);

// 組織情報の更新
export const updateOrganization = createAsyncThunk(
  "organization/update",
  async (_, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState() as RootState;
      const { currentUser, organization: authOrganization } = state.auth;
      const { organization } = state.organization;

      const updateData: UpdateOrganization = {
        name: organization.name,
        url: organization.url,
      };

      const response = await organizationApi.update(currentUser.organization_id, updateData);
      if (response === null) {
        return rejectWithValue("組織情報の更新に失敗しました");
      }

      // 成功時にauthSliceのsetOrganizationを呼び出し
      dispatch(
        setOrganization({
          ...authOrganization,
          name: organization.name,
          url: organization.url,
        }),
      );

      return response;
    } catch (_error) {
      return rejectWithValue("組織情報の更新中にエラーが発生しました");
    }
  },
);

// Create actions outside of the slice
export const removeMemberAction = createAction<string>("organization/removeMember");
export const removeGuestAction = createAction<string>("organization/removeGuest");
export const addMemberAction = createAction<User>("organization/addMember");
export const addGuestAction = createAction<User>("organization/addGuest");
export const updateMemberRoleAction = createAction<{ userId: string; role: string }>(
  "organization/updateMemberRole",
);

const organizationSlice = createSlice({
  name: "organization",
  initialState,
  reducers: {
    // 組織情報の編集用アクション
    setOrganizationName: (state, action: PayloadAction<string>) => {
      state.organization.name = action.payload;
    },
    setOrganizationUrl: (state, action: PayloadAction<string>) => {
      state.organization.url = action.payload;
    },
    setCopyData: (state) => {
      state.copyOrganization = { ...state.organization };
    },
    restoreFromCopyData: (state) => {
      state.organization = { ...state.copyOrganization };
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(removeMemberAction, (state, action) => {
        state.members = state.members.filter((member) => member.id !== action.payload);
      })
      .addCase(removeGuestAction, (state, action) => {
        state.guests = state.guests.filter((guest) => guest.id !== action.payload);
      })
      .addCase(addMemberAction, (state, action) => {
        state.members.push({
          ...action.payload,
          displayRole: handleRenameMemberRole(action.payload.role),
        });
      })
      .addCase(addGuestAction, (state, action) => {
        state.guests.push({
          ...action.payload,
          displayRole: "ゲスト" as DisplayRole,
        });
      })
      .addCase(updateMemberRoleAction, (state, action) => {
        const member = state.members.find((m) => m.id === action.payload.userId);
        if (member) {
          member.role = action.payload.role as User["role"];
          member.displayRole = handleRenameMemberRole(action.payload.role as User["role"]);
        }
      })
      // fetchOrganizationMembers
      .addCase(fetchOrganizationMembers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOrganizationMembers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.members = action.payload.map((member) => ({
          ...member,
          displayRole: handleRenameMemberRole(member.role),
        }));
      })
      .addCase(fetchOrganizationMembers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // fetchOrganizationGuests
      .addCase(fetchOrganizationGuests.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOrganizationGuests.fulfilled, (state, action) => {
        state.isLoading = false;
        state.guests = action.payload.map((guest) => ({
          ...guest,
          displayRole: "ゲスト" as DisplayRole,
        }));
      })
      .addCase(fetchOrganizationGuests.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // fetchOrganization
      .addCase(fetchOrganization.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOrganization.fulfilled, (state, action) => {
        state.isLoading = false;
        state.organization = {
          id: action.payload.id,
          name: action.payload.name,
          url: action.payload.url || "",
          payment_id: action.payload.payment_id || "",
          rootUser: action.payload.rootUser,
        };
      })
      .addCase(fetchOrganization.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // updateOrganization
      .addCase(updateOrganization.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(updateOrganization.fulfilled, (state) => {
        state.isSaving = false;
        state.isEditable = false;
      })
      .addCase(updateOrganization.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setOrganizationName,
  setOrganizationUrl,
  setCopyData,
  restoreFromCopyData,
  clearError,
} = organizationSlice.actions;

export default organizationSlice.reducer;
